import type { PaginationType, SMSTemplatedType, ZoneType } from "@/types"
import Cookies from "js-cookie"
import { z } from "zod"

import type { EmailTemplatedMessagesType } from "@/types/templated-messages"

import { getClientId } from "../auth"
import { apiFetch, formatDate } from "../utils"
import { postActivityLog } from "./log"

const billingUrl = `himo-billing/`

//fetch email templated messages
export async function getEmailTemplatedMessages(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
): Promise<PaginationType<EmailTemplatedMessagesType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}templated-messages/email/?page=${page}&size=${size}&${zoneIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    if (response.status === 404) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginationType<EmailTemplatedMessagesType>
    }
    throw new Error(`Error fetching: ${response.statusText}`)
  }

  const jsonResponse = await response.json()

  return jsonResponse
}

//fetch sms templated messages
export async function getSMSTemplatedMessages(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
): Promise<PaginationType<SMSTemplatedType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}templated-messages/sms/?page=${page}&size=${size}&${zoneIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    if (response.status === 404) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginationType<SMSTemplatedType>
    }
    throw new Error(`Error fetching: ${response.statusText}`)
  }

  const smsResponse = await response.json()
  return smsResponse
}
//Used to Edit/Update Email Templated Messages
export async function editEmailTemplatedMessage(
  emailTemplatedId: number,
  data: EmailTemplatedMessagesType,
) {
  const token = Cookies.get("auth-token")

  if (token) {
    const response = await apiFetch(
      `${billingUrl}templated-messages/email/${emailTemplatedId}/`,
      {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )
    if (!response.ok) {
      await postActivityLog({
        menu_path: "HIMO Billing-Digos Water District-Message Templates",
        sub_menu_path: "/message-templates/email",
        action: "Update Email Template",
        status_code: response.status,
        api_endpoint: response.url,
        web_url: window.location.href,
      })

      throw new Error(`Error: ${response.statusText}`)
    }

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/email",
      action: "Update Email Template",
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    return response.json()
  }

  return null
}

//This endpoint used to create new email templated message
export async function addEmailTemplatedMessage(
  data: EmailTemplatedMessagesType,
) {
  const token = Cookies.get("auth-token")

  if (token) {
    const response = await apiFetch(`${billingUrl}templated-messages/email/`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      await postActivityLog({
        menu_path: "HIMO Billing-Digos Water District-Message Templates",
        sub_menu_path: "/message-templates/email",
        action: "Add Email Template",
        status_code: response.status,
        api_endpoint: response.url,
        web_url: window.location.href,
      })

      let errorMessage = `Error: ${response.statusText}`

      try {
        const errorData = await response.json() // Read the response body
        console.error("API Error Response:", errorData)

        if (errorData.non_field_errors) {
          errorMessage = errorData.non_field_errors.join(", ")
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else if (typeof errorData === "object" && errorData !== null) {
          errorMessage = Object.entries(errorData)
            .map(([key, value]) =>
              Array.isArray(value) ? `${value.join(", ")}` : `${value}`,
            )
            .join("\n") // Newline for better readability
        } else {
          errorMessage = JSON.stringify(errorData) // Fallback
        }
      } catch (parseError) {
        console.error("Error parsing response:", parseError)
      }

      throw new Error(errorMessage)
    }

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/email",
      action: "Add Email Template",
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    return response.json()
  }

  return null
}

//this endpoint use to delete/remove email templated message
export async function deleteEmailTemplatedMessage(id: number) {
  const response = await apiFetch(
    `${billingUrl}templated-messages/email/${id}/`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/email",
      action: `Delete Email Template (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-Message Templates",
    sub_menu_path: "/message-templates/email",
    action: `Delete Email Template  (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get("content-type")
  if (contentType && contentType.includes("application/json")) {
    return response.json()
  } else {
    return null
  }
}

//This endpoint used to create new sms templated message
export async function addSMSTemplatedMessage(data: SMSTemplatedType) {
  const token = Cookies.get("auth-token")

  if (token) {
    const response = await apiFetch(`${billingUrl}templated-messages/sms/`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      await postActivityLog({
        menu_path: "HIMO Billing-Digos Water District-Message Templates",
        sub_menu_path: "/message-templates/sms",
        action: "Add SMS Template",
        status_code: response.status,
        api_endpoint: response.url,
        web_url: window.location.href,
      })

      throw new Error(`Error: ${response.statusText}`)
    }

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/sms",
      action: "Add SMS Template",
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    return response.json()
  }

  return null
}

//Used to Edit/Update sms Templated Messages
export async function editSMSTemplatedMessage(
  smsTemplatedId: number,
  data: EmailTemplatedMessagesType,
) {
  const token = Cookies.get("auth-token")

  if (token) {
    const response = await apiFetch(
      `${billingUrl}templated-messages/sms/${smsTemplatedId}/`,
      {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )
    if (!response.ok) {
      await postActivityLog({
        menu_path: "HIMO Billing-Digos Water District-Message Templates",
        sub_menu_path: "/message-templates/sms",
        action: "Update SMS Template",
        status_code: response.status,
        api_endpoint: response.url,
        web_url: window.location.href,
      })

      throw new Error(`Error: ${response.statusText}`)
    }

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/sms",
      action: "Update SMS Template",
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    return response.json()
  }

  return null
}

//this endpoint use to delete/remove sms templated message
export async function deleteSMSTemplatedMessage(id: number) {
  const response = await apiFetch(
    `${billingUrl}templated-messages/sms/${id}/`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Message Templates",
      sub_menu_path: "/message-templates/sms",
      action: `Delete SMS Template (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-Message Templates",
    sub_menu_path: "/message-templates/sms",
    action: `Delete SMS Template  (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get("content-type")
  if (contentType && contentType.includes("application/json")) {
    return response.json()
  } else {
    return null
  }
}

//this endpoint use to retrieved specific sms templated message
export async function getSMSTemplatedMessage(
  id: number,
): Promise<SMSTemplatedType> {
  const response = await apiFetch(
    `${billingUrl}templated-messages/sms/${id}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching consumer: ${response.statusText}`)
  }
  return response.json()
}

//this endpoint use to retrieved specific email templated message
export async function getEmailTemplatedMessage(
  id: number,
): Promise<SMSTemplatedType> {
  const response = await apiFetch(
    `${billingUrl}templated-messages/email/${id}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching consumer: ${response.statusText}`)
  }
  return response.json()
}
